import { getAllProducts } from 'indices-list'
import { atom } from 'jotai'
import { createPrices } from 'services/price-history'
import { assetConfig } from 'services/price-history/config'
import {
  getPriceRangesStore,
  getPricesStore,
  writePriceRangesStore,
  writePricesStore,
} from 'services/price-history/utils'
import { createAtomsWithQuery } from 'services/query-v2/atomic-factory'
import { QUERY_KEY_USER_HISTORIES } from 'services/query-v2/keys'
import { historicalAssetsStatsQuery } from 'services/query-v2/products/index/daily-statistics'
import { DEFAULT_REFETCH_INTERVAL } from 'services/query-v2/refetch-intervals'
import { noStakingProduct } from 'utils/product-service'
import { Address, isAddressEqual, zeroAddress } from 'viem'
import { getSubgraphNewUrl } from '../../../query/hooks/index'
import { UserPortfolioHistoryItem, UserProductHistory } from './model'
import { getParamsForUserHistory, getProductQueryFetcher } from './query-params'
import { z } from 'zod'
import Big from 'big.js'

const ResponseSchema = z.array(
  z
    .object({
      product: z.object({
        id: z.string(),
        symbol: z.string(),
        basePrice: z.custom(Big),
        decimals: z.number(),
        marketCap: z.custom(Big),
      }),
      balance: z.custom(Big),
      investedValue: z.custom(Big),
      totalSupply: z.custom(Big),
      timestamp: z.number(),
    })
    .optional(),
)

const userHistoryQuery = async <T>({
  queryKey: [key, userAddress],
}: {
  queryKey: [string, Address]
}): Promise<T[]> => {
  const products = getAllProducts()
  const assetsPrices = getPricesStore()

  if (isAddressEqual(userAddress, zeroAddress)) {
    return Promise.resolve([])
  } else {
    const modifiedProducts = products.filter(noStakingProduct)
    const data = await Promise.all(
      modifiedProducts.map(async (product) => {
        const { address, productType } = product
        const productAddress = address as string
        const modifiedSymbol = `${product.network}:${address}`
        if (!assetsPrices[modifiedSymbol]) {
          const portfolio = await historicalAssetsStatsQuery(
            key,
            product.network,
            product.address,
            'userHistoryQuery',
          )
          await createPrices(
            assetConfig,
            portfolio,
            getPricesStore,
            getPriceRangesStore,
            writePricesStore,
            writePriceRangesStore,
          )
        }

        const url = getSubgraphNewUrl(productAddress)
        const queryParams = getParamsForUserHistory(userAddress, productType)
        const fetcher = getProductQueryFetcher(productType)
        const response = await fetcher(url, queryParams, userAddress)
        if (response === null) {
          throw new Error(`Fails in userHistoryQuery function with a key ${key}`)
        }

        const history = ResponseSchema.safeParse(response)
        if (history.success) {
          let filteredHistory: UserPortfolioHistoryItem[] = []
          if (history.data.every((el) => el !== undefined)) {
            filteredHistory = (history.data as UserPortfolioHistoryItem[])
              .filter((el) => isAddressEqual(el.product.id as Address, productAddress as Address))
              .reverse()
          }
          return {
            id: productAddress,
            history: filteredHistory,
          } as T
        }
      }),
    )
    return data as T[]
  }
}

export const userHistoriesFactory = () => {
  const userAddressAtom = atom<Address>(zeroAddress)

  const mainProperties = createAtomsWithQuery<
    UserProductHistory[],
    Address,
    null,
    null,
    [string, Address]
  >(
    { first: userAddressAtom },
    QUERY_KEY_USER_HISTORIES,
    userHistoryQuery<UserProductHistory>,
    DEFAULT_REFETCH_INTERVAL,
    'userHistoryQuery',
  )

  return { userAddress: userAddressAtom, ...mainProperties }
}
